import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  InputBase,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import HomeIcon from '@mui/icons-material/Home';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FolderCopyIcon from '@mui/icons-material/FolderCopy';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import UploadIcon from '@mui/icons-material/Upload';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import CheckIcon from '@mui/icons-material/Check';
import { tokens } from '../../../global/theme/tokens';
import {
  API_DELETE,
  API_GET,
  API_PATCH,
  API_POST,
  DBRequest,
  DBUploadData,
  useConfig,
} from '../../../api/api';
import { hasPermissions } from '../../../auth/hasPermissions';
import Feedback from '../../special/Feedback';
import ExoAlert from '../ExoAlert';
import ExoDialog from '../ExoDialog';
import { formatBytes } from '../explorer/MediaInfo';
import {
  decodeSfmName,
  entryKind,
  entryNameError,
  FOLDER_PURPOSES,
  parentPath,
  pathPrefixes,
} from './explorerUtils';
import ExplorerListView from './ExplorerListView';
import ExplorerColumnView from './ExplorerColumnView';
import ExplorerSearchResults from './ExplorerSearchResults';
import ExplorerFilePreview from './ExplorerFilePreview';
import ExplorerInfoPane from './ExplorerInfoPane';

const SEARCH_SCOPES = [
  { key: 'folder', label: 'This folder', Icon: FolderIcon },
  { key: 'subfolders', label: 'With subfolders', Icon: FolderCopyIcon },
  { key: 'project', label: 'Entire project', Icon: TravelExploreIcon },
];

/**
 * File explorer for a project's network storage. Talks to the
 * `projects/{id}/files*` endpoints (list, download, upload, folder,
 * rename, move, delete).
 */
const ProjectFileExplorer = ({ projectId }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();
  const canEdit = hasPermissions('project', 'write');

  const [view, setView] = useState('list');
  const [path, setPath] = useState('');
  const [history, setHistory] = useState([]);
  const [listing, setListing] = useState(null);
  const [columns, setColumns] = useState([]);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchScope, setSearchScope] = useState('folder');
  const [scopeMenuAnchor, setScopeMenuAnchor] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchVersion, setSearchVersion] = useState(0);
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [infoOpen, setInfoOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [renaming, setRenaming] = useState(null);
  const [newFolderName, setNewFolderName] = useState(null);
  const [menu, setMenu] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [purposeDialog, setPurposeDialog] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [dropOverlay, setDropOverlay] = useState(false);
  const [dragPath, setDragPath] = useState(null);
  const [dragOverPath, setDragOverPath] = useState(null);
  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: '',
  });

  const uploadInputRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchSeqRef = useRef(0);
  // path -> last fetched listing; lets navigation show known folders
  // instantly while the fetch refreshes them silently
  const listingCacheRef = useRef(new Map());
  // every navigation bumps this; directory responses from an older
  // navigation are discarded instead of overwriting the current view
  const navSeqRef = useRef(0);

  // current working directory - upload/new-folder target and breadcrumb base
  const cwd =
    view === 'list'
      ? path
      : columns.length
        ? (columns[columns.length - 1]?.path ?? '')
        : '';

  // "This folder" filters the loaded listing client-side (default, free);
  // the other scopes search recursively through the backend
  const trimmedSearch = query.trim();
  const searchActive = searchScope !== 'folder' && trimmedSearch.length >= 2;

  function closeSearch() {
    setSearchOpen(false);
    setQuery('');
  }

  function toast(text, type = 'success') {
    setAlertState({ alertOpen: true, alertType: type, alertText: text });
  }

  // only surface errors from background requests, never success toasts
  function errorFeedback(message, type) {
    if (type === 'error') toast(message, 'error');
  }

  function fetchDir(dirPath, onDone, withLoading = true, seq = ++navSeqRef.current) {
    const apiSortBy = sortBy === 'kind' ? 'name' : sortBy;
    DBRequest({
      config,
      path:
        `projects/${projectId}/files?path=${encodeURIComponent(dirPath)}` +
        `&sortBy=${apiSortBy}&sortOrder=${sortOrder}`,
      method: API_GET,
      onLoading: withLoading ? setIsLoading : () => {},
      onResponse: (res) => {
        listingCacheRef.current.set(res.data.path ?? dirPath, res.data);
        // a newer navigation superseded this request - keep the cache
        // update but don't touch the view
        if (navSeqRef.current !== seq) return;
        onDone(res.data);
      },
      onFeedback: errorFeedback,
    });
  }

  // initial load + reload when the sort changes
  useEffect(() => {
    if (!config) return;
    if (view === 'list') {
      fetchDir(path, (data) => setListing(data));
    } else {
      reloadColumns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, sortBy, sortOrder]);

  function reloadCurrentView() {
    if (view === 'list') {
      fetchDir(path, (data) => setListing(data));
    } else {
      reloadColumns();
    }
    setSearchVersion((prev) => prev + 1);
  }

  // debounced scoped search
  useEffect(() => {
    if (!config || !searchActive) {
      setSearchResults(null);
      setIsSearching(false);
      return undefined;
    }
    const basePath = searchScope === 'project' ? '' : cwd;
    const seq = ++searchSeqRef.current;
    setIsSearching(true);
    const handle = setTimeout(() => {
      DBRequest({
        config,
        path:
          `projects/${projectId}/files/search?path=${encodeURIComponent(basePath)}` +
          `&query=${encodeURIComponent(trimmedSearch)}`,
        method: API_GET,
        onLoading: () => {},
        onResponse: (res) => {
          if (searchSeqRef.current !== seq) return;
          setIsSearching(false);
          setSearchResults(res.data);
        },
        onFeedback: (message, type) => {
          if (searchSeqRef.current === seq && type === 'error')
            setIsSearching(false);
          errorFeedback(message, type);
        },
      });
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, searchActive, searchScope, trimmedSearch, cwd, searchVersion]);

  function reloadColumns(paths, withLoading = true) {
    // columns form the ancestor chain of the deepest one, so its prefixes
    // are the column paths (null-safe while seeded placeholders load)
    const deepest = [...columns].reverse().find(Boolean);
    const columnPaths = paths || (deepest ? pathPrefixes(deepest.path ?? '') : ['']);
    // one shared sequence for the whole batch of column fetches
    const seq = ++navSeqRef.current;
    columnPaths.forEach((columnPath, index) => {
      fetchDir(
        columnPath,
        (data) => {
          setColumns((prev) => {
            const next = [...prev];
            next[index] = data;
            return next.slice(0, columnPaths.length);
          });
        },
        withLoading,
        seq,
      );
    });
  }

  // ---------- navigation ----------

  function navigateTo(dirPath, pushHistory = true) {
    if (pushHistory) setHistory((prev) => [...prev, path]);
    setSelected(null);
    setRenaming(null);
    closeSearch();
    // known folders render instantly from cache and refresh silently;
    // first visits keep the loading overlay
    const cached = listingCacheRef.current.get(dirPath);
    if (cached) {
      setListing(cached);
      setPath(cached.path ?? dirPath);
    }
    fetchDir(
      dirPath,
      (data) => {
        setListing(data);
        setPath(data.path ?? dirPath);
      },
      !cached,
    );
  }

  function goBack() {
    if (view === 'list') {
      if (!history.length) return;
      const previous = history[history.length - 1];
      setHistory((prev) => prev.slice(0, -1));
      navigateTo(previous, false);
    } else if (columns.length > 1) {
      navSeqRef.current += 1;
      setColumns((prev) => prev.slice(0, -1));
      setSelected(null);
    }
  }

  function handleCrumbClick(prefix, index) {
    if (view === 'list') {
      if (prefix === path) return;
      navigateTo(prefix);
    } else {
      navSeqRef.current += 1;
      setColumns((prev) => prev.slice(0, index + 1));
      setSelected(null);
    }
  }

  function handleViewChange(_, nextView) {
    if (!nextView || nextView === view) return;
    setView(nextView);
    setSelected(null);
    setRenaming(null);
    setNewFolderName(null);
    closeSearch();
    if (nextView === 'column') {
      // seed the columns from cached listings so the switch renders
      // instantly; the fetches refresh silently
      const prefixes = pathPrefixes(path);
      const seeded = prefixes.map(
        (prefix) => listingCacheRef.current.get(prefix) || null,
      );
      setColumns(seeded);
      reloadColumns(prefixes, !seeded.some(Boolean));
    } else {
      // the last column holds the cwd listing - show it right away
      const current = columns[columns.length - 1];
      if (current) setListing(current);
      setPath(cwd);
      setHistory([]);
      fetchDir(cwd, (data) => setListing(data), !current);
    }
  }

  // ---------- selection / preview ----------

  function fetchDownloadUrl(entryPath, onDone) {
    DBRequest({
      config,
      path: `projects/${projectId}/files/download?path=${encodeURIComponent(
        entryPath,
      )}`,
      method: API_GET,
      onLoading: () => {},
      onResponse: (res) => onDone(res.data),
      onFeedback: errorFeedback,
    });
  }

  function handleSelect(entry) {
    setSelected(entry);
    if (!entry.isDir && (!preview || preview.path !== entry.path)) {
      setPreview(null);
      fetchDownloadUrl(entry.path, (data) =>
        setPreview({
          path: entry.path,
          url: data.temporaryUrl,
          mimeType: data.mimeType,
        }),
      );
    }
  }

  // navigate to a directory in whichever view is active (also used to leave
  // search results - clearing the query drops back into normal browsing)
  function jumpTo(dirPath) {
    closeSearch();
    setSelected(null);
    if (view === 'list') {
      navigateTo(dirPath);
    } else {
      const prefixes = pathPrefixes(dirPath);
      const seeded = prefixes.map(
        (prefix) => listingCacheRef.current.get(prefix) || null,
      );
      setColumns(seeded);
      reloadColumns(prefixes, !seeded.some(Boolean));
    }
  }

  function handleOpen(entry) {
    if (entry.isDir) {
      jumpTo(entry.path);
      return;
    }
    handleSelect(entry);
    setPreviewFile(entry);
  }

  function handleColumnOpenDir(entry, columnIndex) {
    setSelected(entry);
    const cached = listingCacheRef.current.get(entry.path);
    if (cached)
      setColumns((prev) => [...prev.slice(0, columnIndex + 1), cached]);
    fetchDir(
      entry.path,
      (data) => {
        setColumns((prev) => {
          if (cached) {
            // silent refresh: only swap the column if it still shows this
            // folder (the user may have navigated on in the meantime)
            if (prev[columnIndex + 1]?.path !== entry.path) return prev;
            return [
              ...prev.slice(0, columnIndex + 1),
              data,
              ...prev.slice(columnIndex + 2),
            ];
          }
          return [...prev.slice(0, columnIndex + 1), data];
        });
      },
      !cached,
    );
  }

  function handleColumnSelect(entry, columnIndex) {
    navSeqRef.current += 1;
    setColumns((prev) => prev.slice(0, columnIndex + 1));
    handleSelect(entry);
  }

  function handleDownload(entry) {
    if (preview && preview.path === entry.path) {
      window.open(preview.url, '_blank');
      return;
    }
    fetchDownloadUrl(entry.path, (data) =>
      window.open(data.temporaryUrl, '_blank'),
    );
  }

  // ---------- mutations ----------

  function handleRenameCommit() {
    if (!renaming) return;
    const name = renaming.value.trim();
    if (!name || name === renaming.entry.name) {
      setRenaming(null);
      return;
    }
    const nameError = entryNameError(name, t);
    if (nameError) {
      toast(nameError, 'error');
      return;
    }
    DBRequest({
      config,
      path: `projects/${projectId}/files/rename`,
      method: API_PATCH({ path: renaming.entry.path, name }),
      onLoading: setIsLoading,
      onResponse: () => {
        setRenaming(null);
        setSelected(null);
        toast(t('Renamed'));
        reloadCurrentView();
      },
      onFeedback: errorFeedback,
    });
  }

  function handleNewFolderCommit() {
    const name = (newFolderName || '').trim();
    if (!name) {
      setNewFolderName(null);
      return;
    }
    const nameError = entryNameError(name, t);
    if (nameError) {
      toast(nameError, 'error');
      return;
    }
    // name travels separately from the parent path so characters like "/"
    // can never be read as path separators by the backend
    DBRequest({
      config,
      path: `projects/${projectId}/files/folder`,
      method: API_POST({ path: cwd, name }),
      onLoading: setIsLoading,
      onResponse: () => {
        setNewFolderName(null);
        toast(t('Folder created'));
        reloadCurrentView();
      },
      onFeedback: errorFeedback,
    });
  }

  function handleDelete(entry) {
    DBRequest({
      config,
      path: `projects/${projectId}/files?path=${encodeURIComponent(entry.path)}`,
      method: API_DELETE(),
      onLoading: setIsLoading,
      onResponse: () => {
        setConfirmDelete(null);
        setSelected(null);
        toast(t('Deleted'));
        reloadCurrentView();
      },
      onFeedback: errorFeedback,
    });
  }

  function handleSetPurposes(entry, purposeKeys) {
    DBRequest({
      config,
      path: `projects/${projectId}/files/purpose`,
      method: API_PATCH({ path: entry.path, purposes: purposeKeys }),
      onLoading: setIsLoading,
      onResponse: () => {
        // keep the dialog open so several purposes can be toggled in a row
        setPurposeDialog((current) =>
          current && current.path === entry.path
            ? { ...current, meta: { ...current.meta, purposes: purposeKeys } }
            : current,
        );
        toast(t('Folder purposes saved'));
        reloadCurrentView();
      },
      onFeedback: errorFeedback,
    });
  }

  function handleTogglePurpose(entry, purposeKey) {
    const current = entry.meta?.purposes || [];
    const next = current.includes(purposeKey)
      ? current.filter((key) => key !== purposeKey)
      : [...current, purposeKey];
    handleSetPurposes(entry, next);
  }

  function handleMove(sourcePath, destinationDir) {
    if (
      sourcePath === destinationDir ||
      destinationDir.startsWith(sourcePath + '/') ||
      parentPath(sourcePath) === destinationDir
    )
      return;
    DBRequest({
      config,
      path: `projects/${projectId}/files/move`,
      method: API_PATCH({ path: sourcePath, destination: destinationDir }),
      onLoading: setIsLoading,
      onResponse: () => {
        setSelected(null);
        toast(t('Moved'));
        reloadCurrentView();
      },
      onFeedback: errorFeedback,
    });
  }

  function handleUpload(fileList, targetDir = cwd) {
    if (!fileList || !fileList.length) return;
    const formData = new FormData();
    formData.append('path', targetDir);
    Array.from(fileList).forEach((file) => formData.append('files[]', file));
    setUploadProgress(0);
    DBUploadData({
      config,
      path: `projects/${projectId}/files/upload`,
      formData,
      onProgressChange: setUploadProgress,
      onResponse: () => {
        setUploadProgress(null);
        toast(t('Uploaded'));
        reloadCurrentView();
      },
      onError: (error) => {
        setUploadProgress(null);
        toast(
          error?.response?.data?.message || t('Unable to upload files'),
          'error',
        );
      },
    });
  }

  // ---------- drag & drop ----------

  const dnd = {
    dragPath,
    dragOverPath,
    canEdit,
    start: (entryPath) => setDragPath(entryPath),
    end: () => {
      setDragPath(null);
      setDragOverPath(null);
    },
    over: (destDir) => setDragOverPath(destDir),
    leave: (destDir) =>
      setDragOverPath((prev) => (prev === destDir ? null : prev)),
    dropOn: (destDir) => {
      if (dragPath && canEdit) handleMove(dragPath, destDir);
      setDragPath(null);
      setDragOverPath(null);
    },
    // OS file drags (uploads) - dropping onto a folder row or a column
    // uploads into that folder instead of the cwd
    isFileDrag: (event) =>
      canEdit &&
      Array.from(event.dataTransfer?.types || []).includes('Files'),
    fileOver: (destDir) => {
      setDragOverPath(destDir);
      setDropOverlay(false);
    },
    fileDrop: (destDir, files) => {
      setDragOverPath(null);
      setDropOverlay(false);
      if (canEdit) handleUpload(files, destDir);
    },
  };

  function handleShellDragOver(event) {
    if (
      canEdit &&
      event.dataTransfer &&
      Array.from(event.dataTransfer.types || []).includes('Files')
    ) {
      event.preventDefault();
      if (!dropOverlay) setDropOverlay(true);
    }
  }

  function handleShellDrop(event) {
    if (event.dataTransfer?.files?.length && canEdit) {
      event.preventDefault();
      setDropOverlay(false);
      handleUpload(event.dataTransfer.files);
    } else {
      setDropOverlay(false);
    }
  }

  // ---------- context menu ----------

  function openMenu(event, entry) {
    event.preventDefault();
    event.stopPropagation();
    setSelected(entry || selected);
    setMenu({ x: event.clientX, y: event.clientY, entry: entry || null });
  }

  function closeMenu() {
    setMenu(null);
  }

  const menuEntry = menu?.entry;
  const menuItems = menuEntry
    ? [
        {
          label: menuEntry.isDir ? t('Open') : t('Open file'),
          Icon: menuEntry.isDir ? FolderOpenIcon : OpenInNewIcon,
          onClick: () => handleOpen(menuEntry),
        },
        {
          label: t('Download'),
          Icon: DownloadIcon,
          onClick: () => handleDownload(menuEntry),
        },
        canEdit &&
          menuEntry.isDir && {
            label: t('Folder purposes'),
            Icon: BookmarkBorderIcon,
            onClick: () => setPurposeDialog(menuEntry),
          },
        canEdit && {
          label: t('Rename'),
          Icon: DriveFileRenameOutlineIcon,
          divider: true,
          onClick: () =>
            setRenaming({ entry: menuEntry, value: menuEntry.name }),
        },
        canEdit && {
          label: t('Delete'),
          Icon: DeleteIcon,
          color: colors.error,
          onClick: () => setConfirmDelete(menuEntry),
        },
      ].filter(Boolean)
    : [
        canEdit && {
          label: t('New Folder'),
          Icon: CreateNewFolderIcon,
          onClick: () => setNewFolderName(''),
        },
        canEdit && {
          label: t('Upload'),
          Icon: UploadIcon,
          onClick: () => uploadInputRef.current?.click(),
        },
      ].filter(Boolean);

  // ---------- derived data ----------

  const crumbPrefixes = pathPrefixes(cwd);
  const activeListing =
    view === 'list' ? listing : columns[columns.length - 1] || null;
  const infoEntry =
    selected ||
    (cwd
      ? { isDir: true, name: cwd.split('/').pop(), path: cwd }
      : { isDir: true, name: t('Project files'), path: '' });

  const canGoBack = view === 'list' ? history.length > 0 : columns.length > 1;

  return (
    <Box
      className="w-full h-full flex flex-col rounded-lg overflow-hidden relative backdrop-blur-lg"
      sx={{
        backgroundColor: colors.glass,
        border: `1px solid ${colors.glass}`,
        minHeight: '480px',
      }}
      onDragOver={handleShellDragOver}
      onDragLeave={(event) => {
        if (dropOverlay && !event.currentTarget.contains(event.relatedTarget))
          setDropOverlay(false);
      }}
      onDrop={handleShellDrop}
    >
      {/* Toolbar */}
      <Box
        className="flex flex-row items-center gap-2 px-2 py-1 flex-wrap"
        sx={{ borderBottom: `1px solid ${colors.glass}` }}
      >
        <IconButton size="small" onClick={goBack} disabled={!canGoBack}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>

        {/* Breadcrumbs */}
        <Box className="flex flex-row items-center gap-1 min-w-0 overflow-x-auto">
          {crumbPrefixes.map((prefix, index) => {
            const isLast = index === crumbPrefixes.length - 1;
            const name =
              index === 0
                ? t('Project files')
                : decodeSfmName(prefix.split('/').pop());
            return (
              <Box
                key={prefix || 'root'}
                className="flex flex-row items-center gap-1 whitespace-nowrap"
              >
                {index > 0 && (
                  <Typography variant="caption" sx={{ opacity: 0.5 }}>
                    /
                  </Typography>
                )}
                <Button
                  size="small"
                  onClick={() => handleCrumbClick(prefix, index)}
                  onDragOver={(event) => {
                    if (dragPath) event.preventDefault();
                  }}
                  onDrop={(event) => {
                    if (dragPath) {
                      event.preventDefault();
                      dnd.dropOn(prefix);
                    }
                  }}
                  startIcon={
                    index === 0 ? (
                      <HomeIcon sx={{ fontSize: '16px' }} />
                    ) : (
                      <FolderIcon sx={{ fontSize: '16px' }} />
                    )
                  }
                  sx={{
                    minWidth: 'unset',
                    textTransform: 'none',
                    color: colors.text,
                    fontWeight: isLast ? 600 : 400,
                    opacity: isLast ? 1 : 0.7,
                    backgroundColor:
                      dragOverPath === prefix && dragPath
                        ? colors.info + '30'
                        : 'transparent',
                  }}
                >
                  {name}
                </Button>
              </Box>
            );
          })}
        </Box>

        <Box className="flex-1" />

        {/* Search - collapsed to an icon, expands on click; scope sits as
            an icon-dropdown inside the right edge of the field */}
        <Box
          className="flex items-center overflow-hidden transition-all duration-300"
          sx={{
            width: searchOpen ? '300px' : '32px',
            minWidth: '32px',
            height: '32px',
            flexShrink: 0,
            bgcolor: searchOpen ? colors.bgInput : 'transparent',
            borderRadius: '8px',
            border: searchOpen
              ? `1px solid ${colors.info}`
              : '1px solid transparent',
            '&:hover': {
              bgcolor: searchOpen ? colors.bgInput : colors.selected,
            },
          }}
          onBlur={(event) => {
            // collapse when focus leaves the field and there is nothing to
            // hold it open (no query, scope menu closed)
            if (
              !event.currentTarget.contains(event.relatedTarget) &&
              !scopeMenuAnchor &&
              !query.trim()
            )
              setSearchOpen(false);
          }}
        >
          {searchOpen ? (
            <>
              {isSearching ? (
                <CircularProgress
                  size={16}
                  sx={{ ml: 1, flexShrink: 0, color: colors.info }}
                />
              ) : (
                <SearchIcon
                  sx={{ fontSize: '18px', ml: 1, opacity: 0.5, flexShrink: 0 }}
                />
              )}
              <InputBase
                inputRef={searchInputRef}
                placeholder={
                  searchScope === 'folder'
                    ? t('Search in this folder')
                    : searchScope === 'subfolders'
                      ? t('Search with subfolders')
                      : t('Search entire project')
                }
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') closeSearch();
                }}
                size="small"
                className="flex-1 px-2"
                autoFocus
                sx={{ fontSize: '13px' }}
              />
              <Tooltip
                title={t(
                  SEARCH_SCOPES.find((scope) => scope.key === searchScope)
                    .label,
                )}
              >
                <IconButton
                  size="small"
                  onClick={(event) => setScopeMenuAnchor(event.currentTarget)}
                  sx={{
                    color:
                      searchScope === 'folder' ? 'inherit' : colors.info,
                  }}
                >
                  {(() => {
                    const ScopeIcon = SEARCH_SCOPES.find(
                      (scope) => scope.key === searchScope,
                    ).Icon;
                    return <ScopeIcon sx={{ fontSize: '16px' }} />;
                  })()}
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={closeSearch} sx={{ mr: 0.25 }}>
                <ClearIcon sx={{ fontSize: '16px' }} />
              </IconButton>
            </>
          ) : (
            <IconButton
              size="small"
              onClick={() => setSearchOpen(true)}
              sx={{ width: '100%', borderRadius: '8px' }}
            >
              <SearchIcon sx={{ fontSize: '18px' }} />
            </IconButton>
          )}
        </Box>

        <Menu
          anchorEl={scopeMenuAnchor}
          open={Boolean(scopeMenuAnchor)}
          onClose={() => setScopeMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {SEARCH_SCOPES.map((scope) => (
            <MenuItem
              key={scope.key}
              selected={scope.key === searchScope}
              onClick={() => {
                setSearchScope(scope.key);
                setScopeMenuAnchor(null);
                // return focus to the input once the menu has closed
                setTimeout(() => searchInputRef.current?.focus(), 0);
              }}
            >
              <ListItemIcon>
                <scope.Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t(scope.label)}</ListItemText>
            </MenuItem>
          ))}
        </Menu>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={handleViewChange}
        >
          <ToggleButton value="list" title={t('List')}>
            <FormatListBulletedIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="column" title={t('Columns')}>
            <ViewColumnIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>

        {canEdit && (
          <Box className="flex flex-row items-center gap-1" sx={{ ml: 1.5 }}>
            <IconButton
              size="small"
              title={t('New Folder')}
              onClick={() => setNewFolderName('')}
            >
              <CreateNewFolderIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              title={t('Upload')}
              onClick={() => uploadInputRef.current?.click()}
            >
              <UploadIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        <input
          ref={uploadInputRef}
          type="file"
          multiple
          hidden
          onChange={(event) => {
            handleUpload(event.target.files);
            event.target.value = '';
          }}
        />

        <IconButton
          size="small"
          title={t('Details')}
          onClick={() => setInfoOpen((prev) => !prev)}
          sx={{
            ml: 1.5,
            backgroundColor: infoOpen ? colors.selected : 'transparent',
          }}
        >
          <ViewSidebarOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>

      {uploadProgress !== null && (
        <LinearProgress variant="determinate" value={uploadProgress} />
      )}

      {/* Content */}
      <Box className="flex-1 min-h-0 flex flex-row relative">
        <Box
          className="flex-1 min-w-0 flex flex-col relative"
          onContextMenu={(event) => openMenu(event, null)}
        >
          {isLoading && (
            <Box
              className="absolute inset-0 flex items-center justify-center z-10"
              sx={{ backgroundColor: colors.backdrop }}
            >
              <CircularProgress size="2rem" />
            </Box>
          )}

          {searchActive ? (
            <ExplorerSearchResults
              results={searchResults}
              isSearching={isSearching}
              selected={selected}
              onSelect={handleSelect}
              onOpen={handleOpen}
              onContextMenu={openMenu}
              onJumpTo={jumpTo}
            />
          ) : view === 'list' ? (
            <ExplorerListView
              listing={listing}
              query={query}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={(key) => {
                if (sortBy === key) {
                  setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
                } else {
                  setSortBy(key);
                  setSortOrder('asc');
                }
              }}
              selected={selected}
              onSelect={handleSelect}
              onOpen={handleOpen}
              onContextMenu={openMenu}
              renaming={renaming}
              onRenamingChange={(value) =>
                setRenaming((prev) => prev && { ...prev, value })
              }
              onRenameCommit={handleRenameCommit}
              onRenameCancel={() => setRenaming(null)}
              newFolderName={newFolderName}
              onNewFolderChange={setNewFolderName}
              onNewFolderCommit={handleNewFolderCommit}
              onNewFolderCancel={() => setNewFolderName(null)}
              dnd={dnd}
            />
          ) : (
            <ExplorerColumnView
              columns={columns}
              query={query}
              selected={selected}
              onOpenDir={handleColumnOpenDir}
              onSelectFile={handleColumnSelect}
              onOpen={handleOpen}
              onContextMenu={openMenu}
              preview={preview}
              onDownload={handleDownload}
              renaming={renaming}
              onRenamingChange={(value) =>
                setRenaming((prev) => prev && { ...prev, value })
              }
              onRenameCommit={handleRenameCommit}
              onRenameCancel={() => setRenaming(null)}
              newFolderName={newFolderName}
              onNewFolderChange={setNewFolderName}
              onNewFolderCommit={handleNewFolderCommit}
              onNewFolderCancel={() => setNewFolderName(null)}
              dnd={dnd}
            />
          )}

          {/* Status bar */}
          <Box
            className="flex flex-row items-center gap-2 px-3 py-1"
            sx={{ borderTop: `1px solid ${colors.glass}` }}
          >
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {searchActive
                ? `${
                    (searchResults?.directories?.length || 0) +
                    (searchResults?.files?.length || 0)
                  } ${t('Results')}`
                : `${
                    (activeListing?.directories?.length || 0) +
                    (activeListing?.files?.length || 0)
                  } ${t('entries')}`}
            </Typography>
          </Box>
        </Box>

        {infoOpen && (
          <ExplorerInfoPane
            entry={infoEntry}
            preview={preview}
            onDownload={handleDownload}
            onPreview={setPreviewFile}
          />
        )}

        {dropOverlay && (
          <Box
            className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none"
            sx={{
              backgroundColor: colors.info + '15',
              border: `2px dashed ${colors.info}`,
              borderRadius: '8px',
            }}
          >
            <Box className="flex flex-col items-center gap-2">
              <CloudUploadIcon sx={{ fontSize: '40px', color: colors.info }} />
              <Typography>
                {t('Drop files to upload to')}{' '}
                <b>{cwd ? cwd.split('/').pop() : t('Project files')}</b>
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Context menu */}
      <Menu
        open={Boolean(menu) && menuItems.length > 0}
        onClose={closeMenu}
        anchorReference="anchorPosition"
        anchorPosition={menu ? { top: menu.y, left: menu.x } : undefined}
        // keep focus on inputs opened from a menu item (rename / new folder):
        // the modal's focus enforcement/restore would steal focus from the
        // autofocused input, whose blur handler then cancels it immediately
        disableEnforceFocus
        disableRestoreFocus
      >
        {menuItems.map((item) => (
          <MenuItem
            key={item.label}
            divider={item.divider}
            onClick={() => {
              closeMenu();
              // run the action after the menu has released focus, so an
              // autofocused input (rename / new folder) is not blurred away
              setTimeout(item.onClick, 0);
            }}
            sx={{ color: item.color || 'inherit' }}
          >
            <ListItemIcon sx={{ color: item.color || 'inherit' }}>
              <item.Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {/* File preview dialog */}
      <ExoDialog
        open={Boolean(previewFile)}
        onClose={() => setPreviewFile(null)}
        limitWidth
        maxWidth="1200px"
      >
        {previewFile && (
          <Box
            className="w-full h-full flex flex-col rounded-lg overflow-hidden"
            sx={{
              backgroundColor: colors.paper,
              border: `1px solid ${colors.glass}`,
            }}
          >
            <Box
              className="flex flex-row items-center gap-2 px-3 py-2"
              sx={{ borderBottom: `1px solid ${colors.glass}` }}
            >
              {(() => {
                const kind = entryKind(previewFile);
                return (
                  <kind.Icon sx={{ fontSize: '18px', color: kind.color }} />
                );
              })()}
              <Box className="min-w-0 flex-1">
                <Typography
                  variant="body2"
                  className="truncate"
                  sx={{ fontWeight: 500 }}
                >
                  {decodeSfmName(previewFile.name)}
                </Typography>
                {Boolean(previewFile.size) && (
                  <Typography variant="caption" sx={{ opacity: 0.6 }}>
                    {formatBytes(previewFile.size)}
                  </Typography>
                )}
              </Box>
              <IconButton
                size="small"
                title={t('Download')}
                onClick={() => handleDownload(previewFile)}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box className="flex-1 min-h-0 overflow-hidden">
              {preview && preview.path === previewFile.path ? (
                <ExplorerFilePreview
                  src={preview.url}
                  mimeType={preview.mimeType || previewFile.mimeType || ''}
                  entry={previewFile}
                />
              ) : (
                <Box className="w-full h-full flex items-center justify-center">
                  <CircularProgress size="2rem" />
                </Box>
              )}
            </Box>
          </Box>
        )}
      </ExoDialog>

      {/* Folder purposes dialog */}
      <Dialog
        open={Boolean(purposeDialog)}
        onClose={() => setPurposeDialog(null)}
      >
        <Box className="p-4 flex flex-col" sx={{ minWidth: '320px' }}>
          <Typography variant="h6">{t('Folder purposes')}</Typography>
          <Typography variant="body2" className="pb-3" sx={{ opacity: 0.7 }}>
            {t(
              'Documents of these types are automatically filed in this folder.',
            )}
          </Typography>
          {FOLDER_PURPOSES.map((purpose) => (
            <MenuItem
              key={purpose.key}
              selected={Boolean(
                purposeDialog?.meta?.purposes?.includes(purpose.key),
              )}
              onClick={() => handleTogglePurpose(purposeDialog, purpose.key)}
              sx={{ borderRadius: '8px' }}
            >
              <ListItemIcon>
                <purpose.Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t(purpose.label)}</ListItemText>
              {purposeDialog?.meta?.purposes?.includes(purpose.key) && (
                <CheckIcon fontSize="small" />
              )}
            </MenuItem>
          ))}
          <MenuItem
            disabled={!purposeDialog?.meta?.purposes?.length}
            onClick={() => handleSetPurposes(purposeDialog, [])}
            sx={{ borderRadius: '8px' }}
          >
            <ListItemIcon>
              <NotInterestedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('None')}</ListItemText>
          </MenuItem>
        </Box>
      </Dialog>

      {/* Delete confirmation */}
      <ExoAlert
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        onSubmit={() => handleDelete(confirmDelete)}
        alert={{
          type: 'warning',
          message: `${t('Permanently delete')} "${decodeSfmName(confirmDelete?.name)}"? ${t(
            'This cannot be undone.',
          )}`,
          submitLabel: t('Delete'),
        }}
      />

      <Feedback state={alertState} setState={setAlertState} />
    </Box>
  );
};

export default ProjectFileExplorer;
